import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { ViewMapDataComponent } from "../../modals/view-data/view-data.component";
import { EditMapDataComponent } from "../../modals/edit-data/edit-data.component";
import { MapDataComponent } from "../../modals/map-data/map-data.component";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { MappingService } from "src/core/services/mapping.service";
import { AsReportedSecurity, MappingFilter } from "src/api/models";
import { ConfirmationService, MessageService } from "primeng/api";
import { SecurityMappingService } from "src/api/services";
import { EditSecurityDataComponent } from "../../modals/edit-security-data/edit-security-data.component";
import { ViewSecurityDataComponent } from "../../modals/view-security-data/view-security-data.component";
import { MapSecurityDataComponent } from "../../modals/map-security-data/map-security-data.component";
import {
  handlePasteWithoutLeadingSpaces,
  preventLeadingSpaceKey,
} from "src/core/utils/input-validation.utils";

@Component({
  selector: "app-security-processed",
  // imports: [],
  templateUrl: "./processed.component.html",
  styleUrl: "./processed.component.css",
  standalone: false,
})
export class SecurityProcessedComponent implements OnChanges {
  @Input() isActive: boolean = false;
  private hasLoadedOnce = false;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isActive"]?.currentValue === true) {
      this.hasLoadedOnce = false;
    }
  }
  ref!: DynamicDialogRef;
  readonly paginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };
  processedSecurity: AsReportedSecurity[] = [];
  selectedIssuers: any[] = [];
  selectedDialogRow: any = null;

  // Pagination for Security
  first = 0;
  rows = 10;
  searchBySecurityType?: string;
  issuerName?: string;
  isLoading: boolean = false;

  constructor(
    private _securityMappingService: SecurityMappingService,
    public dialogService: DialogService,
    private _messageService: MessageService,
    public confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {}
  getProcessedSecurity() {
    const params: MappingFilter = {
      searchByName: this.issuerName,
      searchBySecurityType: this.searchBySecurityType,

      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      status: "PROCESSED",
    };
    this.isLoading = true;
    this._securityMappingService.getSecurityMapping(params).subscribe({
      next: (value) => {
        this.processedSecurity = value.processedSecurity ?? [];
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.paginator.totalCount =
          this.processedSecurity?.[0]?.totalCount ?? 0;
        this.isLoading = false;
      },
    });
  }
  loadData(event: any) {
    if (!this.isActive) return;
    if (!this.hasLoadedOnce && event.first === 0) {
      this.hasLoadedOnce = true;
    } else if (!this.hasLoadedOnce) {
      return;
    }

    if (event.first > 0 && this.paginator.pageSize !== event.rows) {
      this.paginator.pageNumber = 1;
      this.first = 0;
    } else {
      this.paginator.pageNumber = event.first / event.rows + 1;
      this.first = event.first;
    }

    this.paginator.pageSize = event.rows;
    this.getProcessedSecurity();
  }
  onSearch() {
    // Trim string filters before searching
    this.issuerName = this.issuerName?.trim() || undefined;
    this.searchBySecurityType = this.searchBySecurityType?.trim() || undefined;

    this.first = 0;
    this.paginator.pageNumber = 1;
    this.getProcessedSecurity();
  }
  openMapModal(product?: any | any[], event?: Event) {
    if (event) event.preventDefault();
    // Handle both single security and array of securities
    const securities = Array.isArray(product) ? product : [product];
    this.ref = this.dialogService.open(MapSecurityDataComponent, {
      dismissableMask: false,
      closable: true,
      modal: true,
      showHeader: true,
      style: { width: "60vw" },
      contentStyle: { overflow: "auto" },
      header: "Mapping",
      data: {
        securities: securities,
        remap: true,
      },
    });
    this.ref.onClose.subscribe((refresh) => {
      if (refresh) {
        this.getProcessedSecurity();
      }
    });
  }
  openEditMapModal(product?: any, event?: Event) {
    if (event) event.preventDefault();
    const editData = product;

    this.ref = this.dialogService.open(EditSecurityDataComponent, {
      dismissableMask: false,
      closable: true,
      modal: true,
      showHeader: true,
      style: { width: "60vw" },
      contentStyle: { overflow: "auto" },
      data: {
        securityData: editData,
        isEditMode: true,
      },
      header: "Edit Security Data",
    });

    this.ref.onClose.subscribe((refresh) => {
      if (refresh) {
        this.getProcessedSecurity();
      }
    });
  }
  openViewMapModal(product?: any, event?: Event) {
    if (event) event.preventDefault();
    // const viewData = product;

    if (!product?.securityId) {
      this._messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "securityId not found",
      });
      return;
    }
    this.isLoading = true;
    this._securityMappingService
      .getSecurityMappingDetails(product.securityId)
      .subscribe({
        next: (details: AsReportedSecurity[]) => {
          const detailData = details.length > 0 ? details : null;
          this.ref = this.dialogService.open(ViewSecurityDataComponent, {
            dismissableMask: false,
            closable: true,
            modal: true,
            showHeader: true,
            style: { width: "60vw" },
            contentStyle: { overflow: "auto" },
            header: "View Security Details",
            data: {
              securityData: detailData,
              noRecord: !detailData,
            },
          });

          this.ref.onClose.subscribe((result) => {
            if (result?.revertSecurity) {
              this.openMapModal(result.revertSecurity);
            }
            if (result?.revertSecurities && result.revertSecurities.length > 0) {
              this.openMapModal(result.revertSecurities);
            }
          });
        },
        error: (err) => {
          this.isLoading = false;
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              err.error?.message || "Failed to load issuer mapping details",
          });
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  onUpdateClick(product: any) {
    if (product) {
      this.openEditMapModal(product);
    }
  }

  onDetailClick(product: any) {
    if (product) {
      this.openViewMapModal(product);
    }
  }
  onDeleteClick(security: any) {
    this.confirmationService.confirm({
      message: "Are you sure you want to remove this security?",
      header: "Confirm Remove",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const params: SecurityMappingService.RemoveSecurityParams = {
          securityId: security.securityId,
          reason: "Deleted security successfully",
        };

        this.isLoading = true;

        this._securityMappingService.removeSecurity(params).subscribe({
          next: () => {
            this._messageService.add({
              summary: "Security deleted successfully",
              severity: "success",
            });
            this.getProcessedSecurity();
          },
          error: (err) => {
            this.isLoading = false;
            const errorMsg =
              err?.error?.message ||
              "Security ID already mapped, you can't delete it";

            this._messageService.add({
              summary: errorMsg,
              severity: "error",
            });
          },
          complete: () => {
            this.isLoading = false;
          },
        });
      },
    });
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }

  onPaste(
    event: ClipboardEvent,
    field: "issuerName" | "searchBySecurityType"
  ): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      this[field] = newValue;
    });
  }
}
